var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useRef } from 'react';
import { hasValue, generateUUID } from '@utils';
import { format } from 'date-fns';
import useAlert from '@notifications/Alerts/useAlerts';
import { useUXPContext } from '@core/context/useUXPContext';
import { exportEngine } from './exportEngine';
import { exportXlsx } from './exportXlsx';
const DEFAULT_PAGE_SIZE = 100;
/**
 * Sanitizes a file name by removing invalid characters and ensuring a valid name
 * @param fileName - The input file name to sanitize
 * @returns A sanitized file name safe for use
 */
const sanitizeFileName = (fileName) => {
    const defaultName = 'export' + format(new Date(), 'yyyy-MM-dd-HH-mm-ss');
    if (!fileName || typeof fileName !== 'string') {
        return defaultName;
    }
    let sanitized = fileName.replace(/\.[^/.]+$/, '');
    sanitized = sanitized.replace(/[<>:"/\\|?*\x00-\x1f]/g, '');
    sanitized = sanitized.trim().replace(/^\.+|\.+$/g, '');
    sanitized = sanitized.replace(/\s+/g, ' ');
    sanitized = sanitized.substring(0, 100);
    return sanitized || defaultName;
};
/**
 * @export
 * Custom hook for streaming table data to a CSV file with progress tracking
 * and cancellation.
 *
 * The download starts immediately when the export begins and streams as data
 * is fetched; the export keeps running while the user navigates within the
 * app (a "don't refresh" banner shows meanwhile). A hard page reload cancels
 * a running export.
 *
 * @example
 * ```tsx
 * const { exportData, abortExport } = useExportData();
 *
 * <button onClick={() => exportData({
 *   data: async (page, pageSize) => ({ items: await fetchData(page, pageSize) }),
 *   total: async () => await fetchTotal(),
 *   columns: [
 *     { id: 'name', label: 'Name' },
 *     { id: 'status', label: 'Status', formatter: (v) => v == '0' ? 'Active' : 'Inactive' }
 *   ],
 *   pageSize: 500,
 *   fileName: 'data_export'
 * })}>Export Data</button>
 *
 * <button onClick={abortExport}>Cancel Export</button>
 * ```
 */
export const useExportData = () => {
    const alerts = useAlert();
    const context = useUXPContext();
    const currentExportId = useRef(null);
    // Cooperative cancel flag for the client-side xlsx path (CSV uses exportEngine).
    const xlsxAbort = useRef(null);
    const fetchTotal = useCallback((total, query, filters) => __awaiter(void 0, void 0, void 0, function* () {
        if (typeof total === 'number') {
            return total;
        }
        if (typeof total === 'function') {
            return yield total(query, filters);
        }
        return 0;
    }), []);
    const getFileName = useCallback((providedFileName, showPrompt) => __awaiter(void 0, void 0, void 0, function* () {
        const shouldPrompt = !hasValue(providedFileName) || (showPrompt || false);
        if (shouldPrompt) {
            try {
                const formData = yield alerts.form({
                    title: 'Enter file name',
                    formStructure: [
                        {
                            title: '',
                            columns: 1,
                            fields: [
                                {
                                    name: 'name',
                                    label: 'File Name',
                                    type: 'text',
                                    validate: { required: true }
                                }
                            ]
                        }
                    ]
                });
                return (formData === null || formData === void 0 ? void 0 : formData.name) ? sanitizeFileName(formData.name) : null;
            }
            catch (_a) {
                return null;
            }
        }
        return sanitizeFileName(providedFileName);
    }), [alerts]);
    // Build a resolved page-fetcher for a data source (array → slice, fn → call).
    const buildFetchPage = useCallback((source, pageSize, query, filters, sort) => {
        return Array.isArray(source)
            ? (page) => __awaiter(void 0, void 0, void 0, function* () { return ({ items: source.slice((page - 1) * pageSize, page * pageSize) }); })
            : (page) => source(page, pageSize, query, filters, sort);
    }, []);
    const exportData = useCallback((config) => __awaiter(void 0, void 0, void 0, function* () {
        const { data, total, columns, pageSize = DEFAULT_PAGE_SIZE, filters, sort, query, onProgress, fileName = '', showNamePrompt, format, sheets, } = config;
        try {
            const _fileName = yield getFileName(fileName, showNamePrompt);
            if (!_fileName) {
                return;
            }
            // Excel path — multiple sheets and/or format:'xlsx'. Client-side,
            // in-memory workbook (xlsx can't stream through the CSV pipe).
            const useXlsx = format === 'xlsx' || (Array.isArray(sheets) && sheets.length > 0);
            if (useXlsx) {
                const sheetConfigs = (Array.isArray(sheets) && sheets.length > 0)
                    ? sheets
                    : [{ name: _fileName || 'Sheet1', data: data, total: total, columns: columns || [] }];
                const resolved = yield Promise.all(sheetConfigs.map((s) => __awaiter(void 0, void 0, void 0, function* () {
                    return ({
                        name: s.name,
                        columns: s.columns,
                        total: yield fetchTotal(s.total, query, filters),
                        fetchPage: buildFetchPage(s.data, pageSize, query, filters, sort),
                    });
                })));
                const signal = { aborted: false };
                xlsxAbort.current = signal;
                yield exportXlsx({ sheets: resolved, fileName: _fileName, pageSize, onProgress, signal });
                return;
            }
            // CSV path — streamed through the server-side export pipe (unchanged).
            const totalRows = yield fetchTotal(total, query, filters);
            const fetchPage = buildFetchPage(data, pageSize, query, filters, sort);
            const id = generateUUID();
            currentExportId.current = id;
            yield exportEngine.start({
                id,
                fileName: _fileName,
                columns: columns || [],
                pageSize,
                totalRows,
                fetchPage,
                onProgress
            });
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.name) === 'AbortError') {
                alerts.info('Export cancelled');
            }
            else if ((error === null || error === void 0 ? void 0 : error.name) === 'ExportServerUnavailableError') {
                alerts.error(context.$L('uxp-core.text.export-server-unavailable'));
            }
            else {
                alerts.error('Export failed. Please try again.');
            }
        }
        finally {
            currentExportId.current = null;
            xlsxAbort.current = null;
        }
    }), [getFileName, fetchTotal, buildFetchPage, alerts, context]);
    /**
     * Cancels the ongoing export process
     */
    const abortExport = useCallback(() => {
        if (currentExportId.current) {
            exportEngine.abort(currentExportId.current);
        }
        if (xlsxAbort.current) {
            xlsxAbort.current.aborted = true;
        }
    }, []);
    return { exportData, abortExport };
};
